import { useState } from 'react';
import { supabase, GalleryItem } from '../../../lib/supabase';
import ImageCropModal from './ImageCropModal';

interface GalleryItemModalProps {
  item: GalleryItem | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function GalleryItemModal({ item, onClose, onSaved }: GalleryItemModalProps) {
  const [form, setForm] = useState({
    type: item?.type || 'photo',
    url: item?.url || '',
    thumbnail_url: item?.thumbnail_url || '',
    title: item?.title || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [cropImage, setCropImage] = useState<{ file: File; field: 'url' | 'thumbnail_url'; setUploadingFn: (v: boolean) => void } | null>(null);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const uploadImage = async (blob: Blob, field: 'url' | 'thumbnail_url', setUploadingFn: (v: boolean) => void) => {
    setUploadingFn(true);
    setError('');
    try {
      const fileName = `gallery_${Date.now()}_${Math.random().toString(36).slice(2)}.jpg`;
      const { data, error: uploadError } = await supabase.storage
        .from('Fotos')
        .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) {
        setError(`Error al subir la imagen: ${uploadError.message}`);
        return;
      }
      if (data) {
        const { data: urlData } = supabase.storage.from('Fotos').getPublicUrl(data.path);
        setForm(prev => ({ ...prev, [field]: urlData.publicUrl }));
      }
    } catch {
      setError('Error inesperado al subir la imagen.');
    } finally {
      setUploadingFn(false);
    }
  };

  const uploadVideo = async (file: File) => {
    setUploadingVideo(true);
    setError('');
    try {
      const fileName = `video_${Date.now()}_${Math.random().toString(36).slice(2)}.${file.name.split('.').pop()}`;
      const { data, error: uploadError } = await supabase.storage
        .from('Fotos')
        .upload(fileName, file, { upsert: true, contentType: file.type });

      if (uploadError) {
        setError(`Error al subir el vídeo: ${uploadError.message}`);
        return;
      }
      if (data) {
        const { data: urlData } = supabase.storage.from('Fotos').getPublicUrl(data.path);
        setForm(prev => ({ ...prev, url: urlData.publicUrl }));
      }
    } catch {
      setError('Error inesperado al subir el vídeo.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleFileSelect = (file: File, field: 'url' | 'thumbnail_url', setUploadingFn: (v: boolean) => void) => {
    setCropImage({ file, field, setUploadingFn });
  };

  const handleCropConfirm = (croppedBlob: Blob) => {
    if (cropImage) {
      uploadImage(croppedBlob, cropImage.field, cropImage.setUploadingFn);
    }
    setCropImage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) {
      setError('El título y la URL son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');

    const payload = {
      type: form.type,
      url: form.url.trim(),
      thumbnail_url: form.thumbnail_url.trim() || null,
      title: form.title.trim(),
    };

    const { error: dbError } = item
      ? await supabase.from('gallery_items').update(payload).eq('id', item.id)
      : await supabase.from('gallery_items').insert(payload);

    if (dbError) {
      setError('Error al guardar. Inténtalo de nuevo.');
    } else {
      onSaved();
      onClose();
    }
    setSaving(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
        <div className="bg-gray-950 border border-gray-800 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-800">
            <h3 className="text-white font-bold text-base font-['Montserrat']">
              {item ? 'Editar elemento' : 'Nuevo elemento'}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-all cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Type */}
            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">Tipo</label>
              <div className="flex gap-3">
                {(['photo', 'video'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleChange('type', t)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      form.type === t
                        ? t === 'photo'
                          ? 'bg-cyan-500/20 border-cyan-400 text-cyan-400'
                          : 'bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-400'
                        : 'bg-gray-900 border-gray-700 text-gray-500 hover:border-gray-600'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-sm ${t === 'photo' ? 'ri-image-line' : 'ri-play-circle-line'}`}></i>
                    </div>
                    {t === 'photo' ? 'Foto' : 'Vídeo'}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="Ej: Festival de Verano 2024"
                className="w-full bg-gray-900 border border-gray-700 focus:border-cyan-400 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
              />
            </div>

            {/* URL / Upload imagen o video */}
            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">
                {form.type === 'photo' ? 'Imagen *' : 'Vídeo *'}
              </label>

              {form.type === 'photo' ? (
                <div className="space-y-3">
                  {/* Preview */}
                  {form.url && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-800 relative">
                      <img src={form.url} alt="Preview" className="w-full h-full object-cover object-top" />
                      <button
                        type="button"
                        onClick={() => handleChange('url', '')}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                  {/* Botón subir desde PC */}
                  <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    uploading
                      ? 'bg-gray-800 border-gray-700 text-gray-500'
                      : 'bg-cyan-500/20 hover:bg-cyan-500/30 border-cyan-400/50 text-cyan-400'
                  }`}>
                    <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                    {uploading ? 'Subiendo...' : 'Subir desde PC'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleFileSelect(f, 'url', setUploading);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {/* O pegar URL */}
                  <input
                    type="text"
                    value={form.url}
                    onChange={e => handleChange('url', e.target.value)}
                    placeholder="O pega aquí la URL de la imagen"
                    className="w-full bg-gray-900 border border-gray-700 focus:border-cyan-400 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Preview del video */}
                  {form.url && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-800 relative bg-black">
                      {form.url.includes('youtube.com') || form.url.includes('youtu.be') ? (
                        <iframe
                          src={form.url}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <video src={form.url} className="w-full h-full object-cover" controls />
                      )}
                      <button
                        type="button"
                        onClick={() => handleChange('url', '')}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer z-10"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                  {/* Botón subir video desde PC */}
                  <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    uploadingVideo
                      ? 'bg-gray-800 border-gray-700 text-gray-500'
                      : 'bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border-fuchsia-400/50 text-fuchsia-400'
                  }`}>
                    <i className={uploadingVideo ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                    {uploadingVideo ? 'Subiendo vídeo...' : 'Subir vídeo desde PC'}
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      disabled={uploadingVideo}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) uploadVideo(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {/* O pegar URL de YouTube */}
                  <input
                    type="text"
                    value={form.url}
                    onChange={e => handleChange('url', e.target.value)}
                    placeholder="O pega aquí la URL de YouTube (embed)"
                    className="w-full bg-gray-900 border border-gray-700 focus:border-cyan-400 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                  />
                  <p className="text-gray-600 text-xs">
                    Para YouTube usa: https://www.youtube.com/embed/ID_DEL_VIDEO
                  </p>
                </div>
              )}
            </div>

            {/* Thumbnail (solo para vídeo) */}
            {form.type === 'video' && (
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wider mb-2 block">
                  Miniatura del vídeo (opcional)
                </label>
                <div className="space-y-3">
                  {form.thumbnail_url && (
                    <div className="w-full h-28 rounded-lg overflow-hidden border border-gray-800 relative">
                      <img src={form.thumbnail_url} alt="Miniatura" className="w-full h-full object-cover object-top" />
                      <button
                        type="button"
                        onClick={() => handleChange('thumbnail_url', '')}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-black/70 hover:bg-red-500/80 rounded-full text-white text-xs transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}
                  <label className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    uploadingThumb
                      ? 'bg-gray-800 border-gray-700 text-gray-500'
                      : 'bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border-fuchsia-400/50 text-fuchsia-400'
                  }`}>
                    <i className={uploadingThumb ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-line'}></i>
                    {uploadingThumb ? 'Subiendo...' : 'Subir miniatura desde PC'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingThumb}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleFileSelect(f, 'thumbnail_url', setUploadingThumb);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <input
                    type="url"
                    value={form.thumbnail_url}
                    onChange={e => handleChange('thumbnail_url', e.target.value)}
                    placeholder="O pega aquí la URL de la miniatura"
                    className="w-full bg-gray-900 border border-gray-700 focus:border-cyan-400 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-600 outline-none transition-colors"
                  />
                </div>
              </div>
            )}

            {error && (
              <p className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-3">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || uploading || uploadingThumb || uploadingVideo}
                className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] text-white text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Guardando...' : item ? 'Guardar cambios' : 'Añadir elemento'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {cropImage && (
        <ImageCropModal
          imageFile={cropImage.file}
          aspectRatio={cropImage.field === 'url' ? 16 / 9 : undefined}
          onConfirm={handleCropConfirm}
          onCancel={() => {
            cropImage.setUploadingFn(false);
            setCropImage(null);
          }}
          title={cropImage.field === 'url' ? 'Recortar imagen principal' : 'Recortar miniatura'}
        />
      )}
    </>
  );
}